import React, { useEffect, useState } from 'react'
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalFooterAction,
  ModalOverlay
} from '@apex/component-library'

import { BasicDetails, TransferTypeDetails } from './'
import { CashJournalValues, JournalVariation } from '../CashJournalWrapper/Types'
import { buildMoneyFormat } from '../../utils/optionBuilders'
import ModalTransferScheduledPreview from '../ModalTransferScheduledPreview/ModalTransferScheduledPreview'
import { Stack, Box, Text, Flex } from '@chakra-ui/react'
import ModalTransferFeesPreview from '../ModalComponents/ModalTransferFeesPreview'
import TransferAmountWarning from '../TransferAmountWarning'

export interface CashJournalModalProps {
  isModalShowing: boolean
  handleShowingModal: (isOpen: boolean) => void
  handleSubmit: () => void
  fromAccountDisplayText: string
  toAccountDisplayText: string
  formVariation: JournalVariation
  formikValues: CashJournalValues
  hasSubmitPermission: boolean
  isThirdParty?: boolean
  isSubmitting: boolean
  isScheduledTransfer: boolean
  amountAvailable: number
}

export const CashJournalModal = ({
  isModalShowing,
  handleShowingModal,
  handleSubmit,
  fromAccountDisplayText,
  toAccountDisplayText,
  formVariation,
  formikValues,
  hasSubmitPermission,
  isThirdParty,
  isSubmitting,
  amountAvailable
}: CashJournalModalProps) => {
  const { indToIra, iraToInd } = JournalVariation
  const [confirmBtn, setConfirmBtn] = useState<string>('')

  useEffect(() => {
    if (formVariation && formVariation === iraToInd) {
      setConfirmBtn('Distribution')
    } else if (formVariation && formVariation === indToIra) {
      setConfirmBtn('Contribution')
    } else {
      setConfirmBtn('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formVariation])

  return (
    <Modal isCentered motionPreset='slideInBottom' isOpen={isModalShowing} onClose={() => handleShowingModal(false)}>
      <ModalOverlay />
      <ModalContent width='100%' maxW='600px' data-testid='cashJournalModal'>
        <ModalHeader className='test-id-header-1'>
          <Text as='h4'>Cash Journal Transfer</Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody mb={6}>
          <Stack spacing={6}>
            <Text>Please review the details of your transfer below.</Text>
            <Box>
              <BasicDetails
                transferAmount={
                  formikValues.transferAmountRaw
                    ? buildMoneyFormat(formikValues.transferAmountRaw)
                    : 'No transfer amount'
                }
                fromAccount={fromAccountDisplayText}
                toAccount={toAccountDisplayText}
                totalBalanceChecked={formikValues.totalBalanceCheckbox!}
                isThirdParty={isThirdParty}
              />
            </Box>
            <Box>
              <ModalTransferScheduledPreview
                transferRecurrence={formikValues.transferRecurrence!}
                whenToSend={formikValues.whenToSend!}
                transferScheduledDate={formikValues.transferScheduledDate!}
                transferStartDate={formikValues.transferStartDate!}
                transferFrequency={formikValues.transferFrequency!}
                numberOfTransfers={formikValues.numberOfTransfers!}
              />
            </Box>
            <Box>
              {formVariation !== JournalVariation.indToInd && (
                <TransferTypeDetails formikValues={formikValues} formVariation={formVariation} />
              )}
            </Box>
            <Box>
              <ModalTransferFeesPreview fees={formikValues.fees}></ModalTransferFeesPreview>
            </Box>
            <TransferAmountWarning amountAvailable={amountAvailable} transferAmount={formikValues.transferAmountRaw} />
          </Stack>
        </ModalBody>

        <ModalFooterAction>
          <Flex>
            <Box ml='auto'>
              <Button
                variant='white'
                className='test-id-cancel-btn'
                onClick={() => handleShowingModal(false)}
                marginRight='2'
              >
                Cancel
              </Button>
              <Button
                isLoading={isSubmitting}
                isDisabled={!hasSubmitPermission || isSubmitting}
                variant='primary'
                className='test-id-confirm-btn'
                onClick={handleSubmit}
              >
                Confirm {confirmBtn}
              </Button>
            </Box>
          </Flex>
        </ModalFooterAction>
      </ModalContent>
    </Modal>
  )
}
